import {ActionMenu, ActionList, Box, Link, Label} from '@primer/react'
import type {AnchoredOverlayProps, LabelProps} from '@primer/react'
import {isPrivateInstance} from '@github-ui/runtime-environment'
import SignedCommitHeader from './components/SignedCommitHeader'
import {VerifiedIcon, UnverifiedIcon} from '@primer/octicons-react'
import SignedCommitFooter from './components/SignedCommitFooter'
import SmimeCommitFooter from './components/SmimeCommitFooter'
import type {SignatureResult, VerificationStatus} from './signed-commit-types'

export interface SignedCommitBadgeProps {
  hasSignature: boolean
  verificationStatus: VerificationStatus
  signature: SignatureResult
  badgeSize?: LabelProps['size']
  overlayAlign?: AnchoredOverlayProps['align']
}

export function SignedCommitBadge({
  hasSignature,
  verificationStatus,
  signature,
  badgeSize = 'small',
  overlayAlign,
}: SignedCommitBadgeProps) {
  if (verificationStatus === 'unsigned') {
    return <></>
  }

  const {footerLabel, footerUrl} = getCommitSigningHelpLink(signature.helpUrl)

  const reason = signature.signatureVerificationReason.toUpperCase()
  const signedByGitHub = signature.signedByGitHub

  const {icon, icon_color} =
    verificationStatus === 'unverified'
      ? {icon: UnverifiedIcon, icon_color: 'fg.muted'}
      : {icon: VerifiedIcon, icon_color: 'fg.success'}

  let signer = undefined
  if (verificationStatus !== 'unverified' && !signedByGitHub) {
    signer = {avatar_url: signature.signerAvatarUrl, login: signature.signerLogin}
  }

  const signatureLink = (
    <Link href={footerUrl} data-testid="signature-link">
      {footerLabel}
    </Link>
  )

  const anchorProps: {sx: LabelProps['sx']; size: LabelProps['size']} = {sx: {cursor: 'pointer'}, size: badgeSize}
  const verification_label =
    verificationStatus === 'verified' ? (
      <Label variant="success" {...anchorProps}>
        Verified
      </Label>
    ) : verificationStatus === 'unverified' ? (
      <Label variant="attention" {...anchorProps}>
        Unverified
      </Label>
    ) : verificationStatus === 'partially_verified' ? (
      <Label variant="success" {...anchorProps}>
        Partially verified
      </Label>
    ) : (
      <></>
    )

  return (
    <ActionMenu>
      <ActionMenu.Anchor>{verification_label}</ActionMenu.Anchor>

      <ActionMenu.Overlay width="small" align={overlayAlign}>
        <ActionList sx={{p: 0}}>
          <SignedCommitHeader
            reason={reason}
            icon={icon}
            iconColor={icon_color}
            verificationStatus={verificationStatus}
            isViewer={signature.isViewer}
            hasSignature={hasSignature}
            signatureType={signature.signatureType}
            signedByGitHub={signedByGitHub}
            keyExpired={signature.keyExpired}
            keyRevoked={signature.keyRevoked}
          />
          {signature.signatureType === 'GpgSignature' || signature.signatureType === 'SshSignature' ? (
            <SignedCommitFooter
              keyHex={signature.keyId}
              signatureType={signature.signatureType}
              signer={signer}
              showPartiallyVerifiedMessage={verificationStatus === 'partially_verified'}
              signingLinkComponent={signatureLink}
              keyExpired={signature.keyExpired}
              keyRevoked={signature.keyRevoked}
            />
          ) : signature.signatureType === 'SmimeSignature' &&
            signature.signatureCertificateSubject &&
            signature.signatureCertificateIssuer ? (
            <SmimeCommitFooter
              issuer={signature.signatureCertificateIssuer}
              subject={signature.signatureCertificateSubject}
              signingLinkComponent={signatureLink}
            />
          ) : (
            <Box sx={{display: 'flex'}}>
              <Box
                sx={{
                  fontSize: 0,
                  p: 3,
                }}
              >
                {signatureLink}
              </Box>
            </Box>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function getCommitSigningHelpLink(helpUrl: string) {
  if (!isPrivateInstance()) {
    return {
      footerLabel: 'Learn about vigilant mode',
      footerUrl: `${helpUrl}/github/authenticating-to-github/displaying-verification-statuses-for-all-of-your-commits`,
    }
  } else {
    return {footerLabel: 'Learn about signing commits', footerUrl: `${helpUrl}/articles/signing-commits-using-gpg/`}
  }
}

try{ SignedCommitBadge.displayName ||= 'SignedCommitBadge' } catch {}